import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import type { Role } from '../lib/auth';
import { helpDefFor, helpFor, hintSeen, hintsEnabled, markHintSeen, type Help } from './index';

/** The question-mark icon of the "How this works" button. */
function QuestionIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9.5" />
      <path d="M9.6 9.3a2.5 2.5 0 0 1 4.85.85c0 1.65-2.45 2.2-2.45 3.6" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** What the page is for, what you can do, what updates live, one tip: the lines as a short list. */
function HelpBody({ help }: { help: Help }) {
  return (
    <>
      <p className="text-xs text-slate-500">For: {help.forWhom}</p>
      <ul className="mt-2 flex list-disc flex-col gap-1.5 pl-5 text-sm text-slate-700">
        {help.lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </>
  );
}

/**
 * The "How this works" button in the top bar: opens a small sheet with this page's help (a bottom sheet on a
 * phone, a popover under the button on a larger screen). Closes with Escape, the ✕, a tap outside, or when
 * another page opens.
 */
export function HelpButton({ path, role, className }: { path: string; role: Role; className?: string }) {
  const help = helpFor(path, role);
  const [open, setOpen] = useState(false);
  // The address the sheet was opened on. The router shows a new page only once its code has loaded, so the
  // path can change a moment after the address bar did: only a page other than that one closes the sheet.
  const openedAt = useRef<string | null>(null);
  const titleId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && openedAt.current !== path) setOpen(false);
  }, [path, open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!help) return null;
  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          openedAt.current = window.location.pathname;
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="How this works"
        className={className ?? 'flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100'}
      >
        <QuestionIcon />
        <span className="sr-only">How this works</span>
      </button>
      {/*
        Rendered at the end of the page, not inside the top bar: the bar's blurred background makes it the box
        that fixed elements are placed in, which would put the phone's bottom sheet off screen.
      */}
      {open &&
        createPortal(
          <>
            <button type="button" tabIndex={-1} aria-hidden className="fixed inset-0 z-40 cursor-default bg-slate-900/30 sm:bg-transparent" onClick={() => setOpen(false)} />
            <div
              role="dialog"
              aria-labelledby={titleId}
              data-testid="help-sheet"
              className="fixed inset-x-0 bottom-0 z-50 max-h-[75vh] overflow-y-auto rounded-t-2xl border border-slate-200 bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-16 sm:max-h-[calc(100vh-5rem)] sm:w-96 sm:rounded-xl sm:pb-4"
            >
              <div className="flex items-start justify-between gap-2">
                <p id={titleId} className="pt-2.5 text-base font-semibold text-slate-900">
                  How this works: {help.title}
                </p>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close help" className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
                  ✕
                </button>
              </div>
              <HelpBody help={help} />
              <p className="mt-3 border-t border-slate-100 pt-2 text-sm">
                <Link to="/admin/guide" onClick={() => setOpen(false)} className="inline-flex min-h-11 items-center font-medium text-brand-700 hover:underline">
                  See everything I can do →
                </Link>
              </p>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

/**
 * The first time a login opens a page on this device, its help shows once at the top of the page as a small
 * note that can be put away. It is remembered per login and page (visits/:id counts as one page).
 */
export function FirstVisitHint({ path, role, userId }: { path: string; role: Role; userId: string }) {
  const def = helpDefFor(path);
  const key = def?.path ?? null;
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    // Another page's hint disappears by itself (shown !== key); an already seen page shows none.
    if (!key || !hintsEnabled() || hintSeen(userId, key)) return;
    // Shown once: counted as seen the moment it appears, so it does not come back on the next visit.
    markHintSeen(userId, key);
    setShown(key);
  }, [key, userId]);

  const help = helpFor(path, role);
  if (!help || !key || shown !== key) return null;
  return (
    <aside aria-label="How this page works" data-testid="help-hint" className="mb-4 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-brand-900">
      <div className="flex items-start justify-between gap-2">
        <p className="pt-2.5 text-sm font-semibold">New here? How {help.title} works</p>
        <button type="button" onClick={() => setShown(null)} className="-mr-2 inline-flex min-h-11 shrink-0 items-center rounded-lg px-3 text-sm font-medium text-brand-800 hover:bg-white/60">
          Got it
        </button>
      </div>
      <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-sm">
        {help.lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-brand-800/80">Tap the ? at the top of any page to see its help again.</p>
    </aside>
  );
}
